/**
 * Run Workout helpers
 * Shared constants and formatters for run-type workouts in programs.
 *
 * A run workout is a normal workout object with `workoutType: 'run'` and
 * flat run fields (runType, targetMode, distance, effort, ...). Workouts
 * without `workoutType` are treated as 'strength' — fully backwards compatible.
 */

export const RUN_TYPES = [
  'Easy Run',
  'Steady Run',
  'Tempo Run',
  'Interval Session',
  'Long Run',
  'Recovery Run',
  'Fartlek / Speed Play',
  'Hill Repeats'
];

// Effort-based prescriptions (no absolute paces — member self-selects)
export const RUN_EFFORTS = [
  'Easy / Conversational (RPE 2–3)',
  'Steady (RPE 4–5)',
  'Comfortably Hard / Tempo (RPE 6–7)',
  'Hard / Threshold (RPE 7–8)',
  'Very Hard / Interval Effort (RPE 8–9)',
  'Max Effort (RPE 10)'
];

export const DEFAULT_RUN_FIELDS = {
  workoutType: 'strength',
  runType: 'Easy Run',
  targetMode: 'distance', // 'distance' | 'duration' | 'intervals'
  distance: '',
  distanceUnit: 'km',     // 'km' | 'mi'
  runDuration: '',        // minutes (target)
  intervalReps: '',
  intervalDistance: '',
  intervalUnit: 'm',      // 'm' | 'km'
  recovery: '',           // e.g. '400m easy jog'
  effort: RUN_EFFORTS[0]
};

export const emptyWorkoutForm = () => ({
  id: '',
  title: '',
  day: '',
  description: '',
  duration: '',
  difficulty: 'Intermediate',
  equipment: [],
  exercises: [],
  ...DEFAULT_RUN_FIELDS
});

// The headline target, e.g. "12 km" / "45 min" / "8 × 800m"
export const runTargetText = (workout) => {
  if (!workout) return 'Run';
  if (workout.targetMode === 'distance' && workout.distance) {
    return `${workout.distance} ${workout.distanceUnit || 'km'}`;
  }
  if (workout.targetMode === 'duration' && workout.runDuration) {
    return `${workout.runDuration} min`;
  }
  if (workout.targetMode === 'intervals' && workout.intervalReps && workout.intervalDistance) {
    return `${workout.intervalReps} × ${workout.intervalDistance}${workout.intervalUnit || 'm'}`;
  }
  return 'Run';
};

// One-line summary, e.g. "Long Run · 12 km · Easy / Conversational"
export const describeRun = (workout) => {
  if (!workout || workout.workoutType !== 'run') return '';
  const parts = [];
  if (workout.runType) parts.push(workout.runType);
  const target = runTargetText(workout);
  if (target !== 'Run') parts.push(target);
  const effort = (workout.effort || '').split(' (')[0];
  if (effort) parts.push(effort);
  return parts.join(' · ') || 'Run';
};
